import { Timestamp } from '@fullstory/proto/google/protobuf';
import { PageDefinition, SuggestionStatus } from '@fullstory/proto/fs/api/pages';
import { usePageSuggestionTrendData } from '../../hooks/usePageSuggestionTrendData';
import { getPageName, getPageMetadata } from '../../utils/utils';
import { AISuggestedPageBadge } from '../../components/AISuggestedPageBadge';
import React, { PropsWithChildren, useMemo } from 'react';
import { useFlags } from '@fullstory/react-hooks';
import { ObjectDetailsTooltipContent } from '@fullstory/dqm-ui';
import { useSelector } from 'react-redux';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { fullstorySelectors } from '@fullstory/redux';
import { intl } from '@fullstory/intl';
import { Box, Button, Flex, Pane, Pill, Text, Tooltip } from '@fullstory/components';
import { FormattedMessage } from 'react-intl';
import { renderedPageDefPatternRule } from '@fullstory/library-ui';
import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';

export interface PageSuggestionTooltipContentProps {
  pageDefinition: PageDefinition;
  onEdit?: () => void;
}

export const PageSuggestionTooltipContent: React.FC<
  PropsWithChildren<PageSuggestionTooltipContentProps>
> = ({ onEdit, pageDefinition }) => {
  const flags = useFlags();
  const canEdit = flags.hasBit(Flag.PermModifyPageDefinitions);
  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);

  const isPendingSuggestion = pageDefinition.suggestion?.status === SuggestionStatus.STATUS_PENDING;

  const timeRange = useMemo(() => {
    if (showDataDirect) {
      return {
        startTime: 'NOW/DAY-6DAY',
        endTime: 'NOW/DAY+1DAY',
      };
    }
    return {
      startTime: 'NOW/DAY-29DAY',
      endTime: 'NOW/DAY+1DAY',
    };
  }, [showDataDirect]);

  const { trendPoints } = usePageSuggestionTrendData(
    pageDefinition,
    timeRange,
    isPendingSuggestion,
  );

  const activityMonitoringData = useMemo(() => {
    if (isPendingSuggestion && trendPoints) {
      // Use trend data for page suggestions
      return {
        totalEventCount: trendPoints.reduce((sum, curr) => sum + curr.value, 0),
        volume: trendPoints,
        firstSeen: pageDefinition.metadata?.created
          ? Timestamp.toDate(pageDefinition.metadata.created)
          : null,
        lastSeen:
          trendPoints.reduce<Date>((acc, curr) => {
            if (curr.value > 0 && curr.endDate > acc) {
              return curr.endDate;
            }
            return acc;
          }, new Date(0)) ?? null,
        firstSeenLabel: intl.formatMessage({ defaultMessage: 'Suggested on', id: 'GJo5Kx' }),
      };
    } else if (isPendingSuggestion) {
      // Fallback for suggestions without trend data
      return {
        totalEventCount: 0,
        volume: [],
        firstSeen: pageDefinition.metadata?.created
          ? Timestamp.toDate(pageDefinition.metadata.created)
          : null,
        lastSeen: null,
        firstSeenLabel: intl.formatMessage({ defaultMessage: 'Suggested on', id: 'GJo5Kx' }),
      };
    }

    return null;
  }, [trendPoints, pageDefinition.metadata?.created, isPendingSuggestion]);

  if (!pageDefinition || !isPendingSuggestion) {
    return null;
  }

  const urlPatterns = pageDefinition.patterns || [];
  const renderedPatterns = urlPatterns.map(pattern => renderedPageDefPatternRule(pattern));

  return (
    <ObjectDetailsTooltipContent
      data-component="PageSuggestionTooltipContent"
      name={getPageName(pageDefinition)}
      description={pageDefinition.description || ''}
      canEdit={canEdit}
      metadata={getPageMetadata(pageDefinition)}
      activityMonitoring={activityMonitoringData}
      showDataDirect={showDataDirect}
      headerIcon={{ name: 'ai', color: 'ai', size: 20 }}
      objectType="Page"
      extraContent={
        renderedPatterns.length > 0 ? (
          <Flex justifyContent="space-between" alignItems="baseline">
            <Text appearance="bodySmall" color="emphasized">
              {intl.formatMessage({ defaultMessage: 'URL Rules', id: 'gQFPN3' })}
            </Text>
            <Box display="flex" flexDirection="column" gap={4} width="80%">
              {renderedPatterns.map((pattern, i) => (
                <Tooltip key={pattern} tooltipContent={pattern}>
                  <Text appearance="bodySmall" noWrap>
                    <Pill variant="primary">{pattern}</Pill>
                  </Text>
                </Tooltip>
              ))}
            </Box>
          </Flex>
        ) : undefined
      }
      footerContent={
        <Pane
          minHeight={66}
          padding={16}
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          backgroundColor="secondary"
        >
          <AISuggestedPageBadge />
          <Button variant="fill-primary" onClick={onEdit} disabled={!canEdit} size="small">
            <FormattedMessage defaultMessage="Review" id="R+J5ox" />
          </Button>
        </Pane>
      }
      eventSupport={DQMRow.EventSupport.EVENT_SUPPORT_SUPPORTED}
    />
  );
};
